import React, { useState, useEffect } from 'react';
import { User, Star, Phone, LogOut, X, MessageSquare, Loader2, ChevronDown, ChevronUp, Briefcase, Heart, MessageCircle, CheckCircle, ShieldCheck, HelpCircle, ArrowRight, Check } from 'lucide-react';
import { auth, db } from './firebase';
import { signOut } from "firebase/auth";
import { collection, query, where, onSnapshot, doc, updateDoc, getDoc, arrayRemove, arrayUnion, addDoc, serverTimestamp, increment } from "firebase/firestore";
import SEO from './SEO'; 

// רכיבים נוספים
import VerificationUpload from './components/VerificationUpload';
import SupportForm from './components/SupportForm';
import CourierModePanel from './CourierModePanel';

// פונקציה לחישוב גיל
const getAge = (timestamp) => {
  if (!timestamp) return "";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const m = today.getMonth() - date.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < date.getDate())) {
      age--;
  }
  return age;
};

// --- רכיב עזר: שורה של מועמד (לשימוש ברשימת המשימות) ---
const CandidateRow = ({ candidateId, taskId, onApprove, onReject, onViewProfile }) => {
    const [candidateData, setCandidateData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCandidate = async () => {
            try {
                const docRef = doc(db, "users", candidateId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setCandidateData({ uid: docSnap.id, ...docSnap.data() });
                }
            } catch (e) {
                console.error("Error fetching candidate", e);
            } finally {
                setLoading(false);
            }
        };
        fetchCandidate();
    }, [candidateId]);

    if (loading) return <div className="p-2"><Loader2 className="animate-spin w-4 h-4"/></div>;
    if (!candidateData) return null;

    return (
        <div className="flex items-center justify-between bg-white p-3 rounded-xl border border-gray-100 shadow-sm mb-2">
            <div className="flex items-center gap-3">
                {candidateData.profileImage ? (
                    <img src={candidateData.profileImage} className="w-10 h-10 rounded-full object-cover border border-gray-200" />
                ) : (
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center"><User size={16}/></div>
                )}
                <div>
                    <p className="font-bold text-sm text-gray-800">{candidateData.fullName}</p>
                    <div className="flex items-center gap-1 text-xs text-yellow-500">
                        <Star size={10} fill="currentColor"/> 
                        <span>{candidateData.ratingSum > 0 ? (candidateData.ratingSum / candidateData.reviewsCount).toFixed(1) : "חדש"}</span>
                        <span className="text-gray-400">({candidateData.reviewsCount})</span>
                    </div>
                </div>
            </div>
            
            <div className="flex gap-2 items-center">
                <button onClick={() => onViewProfile(candidateData)} className="bg-gray-100 p-2 rounded-full text-gray-600 hover:bg-gray-200">
                    <MessageSquare size={16} />
                </button>
                <button onClick={() => onReject(taskId, candidateId)} className="bg-red-50 text-red-500 p-2 rounded-full hover:bg-red-100 border border-red-100">
                    <X size={16} />
                </button>
                <button onClick={() => onApprove(taskId, candidateId, candidateData)} className="bg-black text-white px-3 py-1.5 rounded-full text-xs font-bold hover:bg-green-600 transition-colors shadow-sm">
                    אשר
                </button>
            </div>
        </div>
    );
};

// --- קומפוננטת הפרופיל הראשית ---
export default function Profile({ userData, onClose, isOnline, onToggleOnline }) {
  const [activeTab, setActiveTab] = useState('created'); 
  const [myCreatedTasks, setMyCreatedTasks] = useState([]);
  const [myApplications, setMyApplications] = useState([]); 
  const [expandedTask, setExpandedTask] = useState(null);
  
  // משתנים לצפייה בביקורות
  const [viewingUser, setViewingUser] = useState(null); 
  const [userReviews, setUserReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  // מודאל סיום משימה ודירוג
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [taskToReview, setTaskToReview] = useState(null);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);

  // --- מצב תמיכה (חדש) ---
  const [showSupport, setShowSupport] = useState(false);

  // Schema ל-SEO
  const profileSchema = userData ? {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": userData.fullName,
    "image": userData.profileImage,
    "description": `נותן שירות באפליקציית דרייבר.ישראל`,
    "aggregateRating": userData.reviewsCount > 0 ? {
      "@type": "AggregateRating",
      "ratingValue": (userData.ratingSum / userData.reviewsCount).toFixed(1),
      "reviewCount": userData.reviewsCount
    } : undefined
  } : null;

  // 1. שליפת משימות שפרסמתי
  useEffect(() => {
    if (!userData) return;
    const q = query(collection(db, "tasks"), where("creatorId", "==", userData.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const tasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      tasks.sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds);
      setMyCreatedTasks(tasks);
    });
    return () => unsubscribe();
  }, [userData]);

  // 2. שליפת משימות שנרשמתי אליהן
  useEffect(() => {
    if (!userData) return;
    const q = query(collection(db, "tasks"), where("candidates", "array-contains", userData.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
        const tasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        tasks.sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds);
        setMyApplications(tasks);
    });
    return () => unsubscribe();
  }, [userData]);

  // 3. שליפת ביקורות
  useEffect(() => {
    if (!viewingUser) {
        setUserReviews([]);
        return;
    }
    setReviewsLoading(true);
    const reviewsRef = collection(db, "users", viewingUser.uid, "reviews");
    const q = query(reviewsRef);
    const unsubscribe = onSnapshot(q, (snapshot) => {
        const fetchedReviews = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        fetchedReviews.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setUserReviews(fetchedReviews);
        setReviewsLoading(false);
    });
    return () => unsubscribe();
  }, [viewingUser]);

  const openWhatsApp = (phone) => {
    if (!phone) return;
    let cleanPhone = phone.replace(/[^\d]/g, '');
    if (cleanPhone.startsWith('0')) cleanPhone = '972' + cleanPhone.slice(1);
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  const handleLogout = () => {
    signOut(auth);
    onClose();
  };

  const handleApproveCandidate = async (taskId, candidateId, candidateData) => {
      if (!window.confirm(`האם לאשר את ${candidateData.fullName} למשימה?`)) return;
      try {
          const taskRef = doc(db, "tasks", taskId);
          await updateDoc(taskRef, {
              status: 'taken',
              candidateId: candidateId, 
              candidateName: candidateData.fullName,
              candidatePhone: candidateData.phone || "לא צוין"
          });
          alert("המועמד אושר בהצלחה! המשימה עברה לסטטוס 'בביצוע'.");
      } catch (error) {
          console.error("Error approving:", error);
          alert("שגיאה באישור");
      }
  };

  const handleRejectCandidate = async (taskId, candidateId) => {
      if (!window.confirm("האם לדחות את המועמד? הוא לא יוכל לראות את המשימה שוב.")) return;
      try {
          const taskRef = doc(db, "tasks", taskId);
          await updateDoc(taskRef, {
              candidates: arrayRemove(candidateId),
              rejectedCandidates: arrayUnion(candidateId),
              status: 'open' 
          });
      } catch (error) {
          console.error("Error rejecting:", error);
          alert("שגיאה בדחייה");
      }
  };

  const openReviewModal = (task) => {
      setTaskToReview(task);
      setRating(5);
      setReviewText("");
      setReviewModalOpen(true);
  };

  const handleSubmitReview = async () => {
      if (!taskToReview || !reviewText) return alert("אנא כתוב כמה מילים על העוזר");
      setSubmittingReview(true);

      try {
          const helperId = taskToReview.candidateId;
          
          await addDoc(collection(db, "users", helperId, "reviews"), {
              reviewerName: userData.fullName,
              rating: rating,
              text: reviewText,
              taskId: taskToReview.id,
              createdAt: serverTimestamp()
          });

          const helperRef = doc(db, "users", helperId);
          await updateDoc(helperRef, {
              ratingSum: increment(rating),
              reviewsCount: increment(1)
          });

          const taskRef = doc(db, "tasks", taskToReview.id);
          await updateDoc(taskRef, {
              status: 'done'
          });

          alert("המשימה הסתיימה והביקורת נשלחה בהצלחה! 🌟");
          setReviewModalOpen(false);

      } catch (error) {
          console.error("Error submitting review:", error);
          alert("שגיאה בשליחת הביקורת: " + error.message);
      } finally {
          setSubmittingReview(false);
      }
  };

  // --- מצב תמיכה (מחליף את התצוגה אם המשתמש לחץ על עזרה) ---
  if (showSupport) {
      return (
          <SupportForm 
             user={userData} 
             onClose={() => setShowSupport(false)} 
             onSuccess={() => {setShowSupport(false); onClose();}} 
          />
      );
  }

  // --- תצוגת הפרופיל הראשית ---
  return (
    <div className="h-full bg-gray-50 flex flex-col text-right overflow-y-auto" dir="rtl">
      
      {profileSchema && <SEO title={`הפרופיל של ${userData?.fullName}`} description="פרופיל משתמש" schemaData={profileSchema} />}

      {/* כותרת פרופיל */}
      <div className="bg-white p-6 pb-6 rounded-b-[40px] shadow-sm mb-4 relative z-10">
          <div className="flex justify-between items-start mb-4">
            <button onClick={handleLogout} className="text-red-500 bg-red-50 p-2 rounded-full"><LogOut size={18}/></button>
            <button onClick={onClose} className="bg-gray-100 p-2 rounded-full"><X size={20}/></button>
          </div>

          <div className="text-center">
            <div className="relative inline-block mb-3">
                {userData?.profileImage ? (
                    <img src={userData.profileImage} className="w-24 h-24 rounded-full object-cover border-4 border-gray-50 shadow-md"/>
                ) : (
                    <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto"><User size={40} className="text-gray-400"/></div>
                )}
                {userData?.isIdentityVerified && <div className="absolute bottom-0 right-0 bg-green-500 text-white p-1.5 rounded-full border-2 border-white"><Check size={12}/></div>}
            </div>
            
            {/* שם וגיל */}
            <div className="flex flex-col items-center gap-1">
                <h2 className="text-2xl font-black text-gray-900">{userData?.fullName || "אורח"}</h2>
                {userData?.birthDate && (
                    <span className="bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full font-bold mt-1">
                        בן {getAge(userData.birthDate)}
                    </span>
                )}
            </div>

            <div className="flex justify-center gap-4 mt-4">
                <div className="text-center bg-gray-50 px-4 py-2 rounded-xl">
                    <span className="block font-bold text-xl">{userData?.ratingSum > 0 ? (userData.ratingSum / userData.reviewsCount).toFixed(1) : "0"}</span>
                    <span className="text-xs text-gray-400">דירוג</span>
                </div>
            </div>
          </div>

          {/* טאבים */}
          <div className="flex bg-gray-100 p-1 rounded-2xl mt-6">
              <button 
                onClick={() => setActiveTab('created')} 
                className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${activeTab === 'created' ? 'bg-white shadow-sm text-black' : 'text-gray-400'}`}
              >
                  <Briefcase size={16}/> מה שפרסמתי
              </button>
              <button 
                onClick={() => setActiveTab('applications')} 
                className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${activeTab === 'applications' ? 'bg-white shadow-sm text-black' : 'text-gray-400'}`}
              >
                  <Heart size={16}/> מה שלקחתי
              </button>
          </div>
      </div>

      {/* --- פאנל מצב שליח (דרייבר.ישראל) --- */}
      <div className="px-5 mb-4">
          <CourierModePanel userData={userData} isOnline={isOnline} onToggleOnline={onToggleOnline} />
      </div>

      {/* --- גוף הפרופיל (היסטוריית משלוחים / משימות) --- */}
      <div className="px-5 pb-8 flex-1">
          {activeTab === 'created' && (
              <div className="animate-in slide-in-from-right duration-300">
                  {myCreatedTasks.length === 0 ? (
                      <div className="text-center text-gray-400 py-10 bg-white rounded-2xl border border-dashed border-gray-300">
                          <p>עדיין לא פרסמת משימות</p>
                      </div>
                  ) : (
                      <div className="space-y-4">
                          {myCreatedTasks.map(task => (
                              <div key={task.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                  {/* כותרת משימה */}
                                  <div className="p-4 flex justify-between items-center cursor-pointer" onClick={() => setExpandedTask(expandedTask === task.id ? null : task.id)}>
                                      <div>
                                          <div className="flex items-center gap-2 mb-1">
                                              <span className={`w-2 h-2 rounded-full ${task.status === 'open' ? 'bg-green-500' : task.status === 'taken' ? 'bg-blue-500' : task.status === 'done' ? 'bg-gray-400' : 'bg-orange-400'}`}></span>
                                              <h4 className={`font-bold ${task.status === 'done' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{task.title}</h4>
                                          </div>
                                          <p className="text-xs text-gray-500">
                                              {task.status === 'open' ? 'פתוח' : task.status === 'taken' ? 'בביצוע' : task.status === 'done' ? 'הסתיים' : 'ממתין'}
                                              {task.status === 'open' && ` • ${task.candidates?.length || 0} הצעות`}
                                          </p>
                                      </div>
                                      {expandedTask === task.id ? <ChevronUp size={20} className="text-gray-400"/> : <ChevronDown size={20} className="text-gray-400"/>}
                                  </div>

                                  {/* אזור פירוט מועמדים */}
                                  {expandedTask === task.id && (
                                      <div className="bg-gray-50 p-4 border-t border-gray-100">
                                          {(task.status === 'taken' || task.status === 'done') ? (
                                              <div className="space-y-4">
                                                  <div className={`p-3 rounded-xl text-sm font-bold flex items-center gap-2 border ${task.status === 'done' ? 'bg-gray-50 text-gray-600 border-gray-200' : 'bg-blue-50 text-blue-900 border-blue-100'}`}>
                                                      <Check size={16}/> {task.status === 'done' ? 'בוצע ע"י' : 'בביצוע ע"י'} {task.candidateName}
                                                      <div className="mr-auto flex gap-2">
                                                          <button onClick={() => openWhatsApp(task.candidatePhone)} className="bg-green-500 text-white p-1.5 rounded-full shadow hover:scale-105 transition-transform"><MessageCircle size={16}/></button>
                                                          <a href={`tel:${task.candidatePhone}`} className="bg-blue-500 text-white p-1.5 rounded-full shadow hover:scale-105 transition-transform"><Phone size={16}/></a>
                                                      </div>
                                                  </div>
                                                  {task.status === 'taken' && (
                                                      <button 
                                                          onClick={() => openReviewModal(task)}
                                                          className="w-full bg-emerald-600 text-white py-4 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2 active:scale-95 transition-all"
                                                      >
                                                          <CheckCircle size={20}/> המשימה בוצעה? דרג את העוזר וסיים
                                                      </button>
                                                  )}
                                                  {task.status === 'done' && (
                                                      <div className="text-center text-gray-400 text-xs mt-2">
                                                          <p>המשימה הסתיימה והביקורת נשלחה.</p>
                                                      </div>
                                                  )}
                                              </div>
                                          ) : (
                                              /* משימה פתוחה */
                                              <>
                                                  {(!task.candidates || task.candidates.length === 0) ? (
                                                      <p className="text-sm text-gray-500 text-center py-2">עדיין אין מתנדבים.</p>
                                                  ) : (
                                                      <div className="space-y-2">
                                                          {task.candidates.map(uid => (
                                                              <CandidateRow 
                                                                  key={uid} 
                                                                  candidateId={uid} 
                                                                  taskId={task.id}
                                                                  onApprove={handleApproveCandidate}
                                                                  onReject={handleRejectCandidate}
                                                                  onViewProfile={(u) => setViewingUser(u)}
                                                              />
                                                          ))}
                                                      </div>
                                                  )}
                                              </>
                                          )}
                                      </div>
                                  )}
                              </div>
                          ))}
                      </div>
                  )}
              </div>
          )}

          {activeTab === 'applications' && (
              <div className="animate-in slide-in-from-left duration-300">
                  {myApplications.length === 0 ? (
                      <div className="text-center text-gray-400 py-10 bg-white rounded-2xl border border-dashed border-gray-300">
                          <p>עדיין לא נרשמת למשימות</p>
                      </div>
                  ) : (
                      <div className="space-y-4">
                          {myApplications.map(task => {
                              const isApproved = task.status === 'taken' && task.candidateId === userData.uid;
                              const isDone = task.status === 'done' && task.candidateId === userData.uid;
                              const isRejected = (task.status === 'taken' || task.status === 'done') && task.candidateId !== userData.uid;
                              
                              return (
                                  <div key={task.id} className={`bg-white rounded-2xl shadow-sm border overflow-hidden ${isApproved ? 'border-green-500 ring-1 ring-green-500' : 'border-gray-100'}`}>
                                      <div className="p-4">
                                          <div className="flex justify-between items-start mb-2">
                                              <h4 className="font-bold text-gray-900">{task.title}</h4>
                                              <span className="text-xs bg-gray-100 px-2 py-1 rounded-lg text-gray-600">{new Date(task.createdAt?.seconds * 1000).toLocaleDateString()}</span>
                                          </div>

                                          {isDone ? (
                                              <div className="mt-2 bg-gray-100 p-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-bold flex items-center gap-2">
                                                  <CheckCircle size={16}/> המשימה הסתיימה. כל הכבוד!
                                              </div>
                                          ) : isApproved ? (
                                              <div className="mt-3 bg-green-50 p-3 rounded-xl border border-green-100">
                                                  <p className="text-green-700 font-bold text-sm flex items-center gap-2 mb-2">
                                                      <Check size={16}/> התקבלת למשימה!
                                                  </p>
                                                  <div className="flex items-center gap-3 bg-white p-2 rounded-lg border border-green-100 shadow-sm">
                                                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                                                          {task.creatorImage ? <img src={task.creatorImage} className="w-full h-full object-cover"/> : <User size={20}/>}
                                                      </div>
                                                      <div>
                                                          <p className="font-bold text-sm text-gray-800">{task.creatorName}</p>
                                                          <p className="text-xs text-gray-500">יוצר המשימה</p>
                                                      </div>
                                                      
                                                      <div className="mr-auto flex gap-2">
                                                          <button onClick={() => openWhatsApp(task.creatorPhone)} className="bg-green-500 text-white p-2 rounded-full shadow-lg hover:scale-105 transition-transform"><MessageCircle size={18}/></button>
                                                          <a href={`tel:${task.creatorPhone}`} className="bg-blue-600 text-white p-2 rounded-full shadow-lg hover:scale-105 transition-transform"><Phone size={18}/></a>
                                                      </div>
                                                  </div>
                                              </div>
                                          ) : isRejected ? (
                                              <div className="mt-2 bg-gray-50 p-2 rounded-lg text-xs text-gray-400 flex items-center gap-2">
                                                  <X size={14}/> המשימה נתפסה על ידי מישהו אחר.
                                              </div>
                                          ) : (
                                              <div className="mt-2 bg-orange-50 p-3 rounded-xl border border-orange-100 text-orange-700 text-sm font-bold flex items-center gap-2">
                                                  <Loader2 size={16} className="animate-spin"/> הבקשה נשלחה, ממתין לאישור...
                                              </div>
                                          )}
                                      </div>
                                  </div>
                              );
                          })}
                      </div>
                  )}
              </div>
          )}
      </div>

      {/* --- אזור תחתון: תמיכה ואימות --- */}
      <div className="px-5 pb-20 mt-4 border-t border-gray-100 pt-6">
          
          {/* כפתור תמיכה */}
          <button 
              onClick={() => setShowSupport(true)}
              className="w-full mb-6 bg-blue-50 text-blue-700 py-3 rounded-xl font-bold flex items-center justify-between px-4 border border-blue-100 hover:bg-blue-100 transition-colors"
          >
             <span className="flex items-center gap-2"><HelpCircle size={20}/> תמיכה ודיווח</span>
             <ArrowRight size={16} className="opacity-50"/>
          </button>

          {/* אזור אימות זהות (לא חובה לפרופיל אבל נמצא כאן למקרה שצריך) - אם אתה רוצה למחוק לגמרי תמחק מכאן ועד הסוגר של ה-div */}
          {/* בגרסה שביקשת הסרנו את האימות מכאן כדי שיהיה רק בהרשמה, אבל השארתי את הקוד כאופציה למקרה שתרצה להחזיר אותו. אם אתה לא רוצה אותו - פשוט תמחק את הבלוק הבא */}
          {/* <div>
             <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 justify-center">
                 <ShieldCheck size={18} className="text-gray-400"/> סטטוס חשבון
             </h3>
             ...
          </div> 
          */}
      </div>

      {/* מודאל סיום משימה ודירוג */}
      {reviewModalOpen && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
              <div className="bg-white w-full max-w-sm rounded-3xl p-6 shadow-2xl relative">
                  <button onClick={() => setReviewModalOpen(false)} className="absolute top-4 left-4 bg-gray-100 p-2 rounded-full"><X size={20}/></button>
                  
                  <h3 className="text-xl font-black text-center mb-2">סיום משימה</h3>
                  <p className="text-sm text-gray-500 text-center mb-6">איך היה השירות של {taskToReview?.candidateName}?</p>

                  <div className="flex justify-center gap-2 mb-6">
                      {[1, 2, 3, 4, 5].map((star) => (
                          <button key={star} onClick={() => setRating(star)} className="transition-transform hover:scale-110">
                              <Star size={32} fill={star <= rating ? "#EAB308" : "none"} className={star <= rating ? "text-yellow-500" : "text-gray-300"} />
                          </button>
                      ))}
                  </div>

                  <textarea 
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    placeholder="כתוב כמה מילים חמות (או הערות לשיפור)..."
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 h-24 mb-6 outline-none focus:ring-2 focus:ring-black text-sm"
                  />

                  <button 
                    onClick={handleSubmitReview}
                    disabled={submittingReview}
                    className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2 active:scale-95 transition-all"
                  >
                      {submittingReview ? <Loader2 className="animate-spin"/> : "שלח דירוג וסיים"}
                  </button>
              </div>
          </div>
      )}

      {/* מודאל צפייה בביקורות */}
      {viewingUser && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
              <div className="bg-white w-full max-w-sm rounded-3xl p-6 shadow-2xl relative max-h-[80vh] flex flex-col">
                  <button onClick={() => setViewingUser(null)} className="absolute top-4 left-4 bg-gray-100 p-2 rounded-full hover:bg-gray-200"><X size={20}/></button>
                  
                  <div className="text-center mb-6">
                      <img src={viewingUser.profileImage || ""} className="w-20 h-20 rounded-full mx-auto mb-3 bg-gray-200 object-cover border-2 border-white shadow-lg"/>
                      <h3 className="font-bold text-xl">{viewingUser.fullName}</h3>
                      <div className="flex items-center justify-center gap-1 text-yellow-500 mt-1 font-bold">
                        <Star fill="currentColor" size={16}/>
                        <span>{viewingUser.ratingSum > 0 ? (viewingUser.ratingSum / viewingUser.reviewsCount).toFixed(1) : "חדש"}</span>
                      </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-2xl flex-1 overflow-y-auto mb-4 border border-gray-100">
                      <div className="flex justify-between items-center mb-3">
                          <h4 className="font-bold text-sm text-gray-800">ביקורות ({userReviews.length})</h4>
                      </div>
                      
                      {reviewsLoading ? (
                          <div className="flex justify-center py-4"><Loader2 className="animate-spin text-gray-400"/></div>
                      ) : userReviews.length === 0 ? (
                          <div className="text-center py-6 text-gray-400 text-sm">
                              <p>לא נמצאו ביקורות במערכת.</p>
                          </div>
                      ) : (
                          <div className="space-y-3">
                              {userReviews.map(review => (
                                  <div key={review.id} className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm text-sm animate-in slide-in-from-bottom-2">
                                      <div className="flex justify-between items-start mb-1">
                                          <span className="font-bold text-gray-800">{review.reviewerName || "אנונימי"}</span>
                                          <div className="flex text-yellow-500 text-xs gap-0.5">
                                              {[...Array(5)].map((_, i) => (
                                                  <Star key={i} size={10} fill={i < review.rating ? "currentColor" : "none"} className={i < review.rating ? "text-yellow-500" : "text-gray-300"}/>
                                              ))}
                                          </div>
                                      </div>
                                      <p className="text-gray-600 leading-relaxed text-xs">{review.text || review.comment}</p>
                                      <span className="text-[10px] text-gray-300 mt-2 block text-left">
                                          {review.createdAt ? new Date(review.createdAt.seconds * 1000).toLocaleDateString() : "עכשיו"}
                                      </span>
                                  </div>
                              ))}
                          </div>
                      )}
                  </div>

                  <button onClick={() => setViewingUser(null)} className="w-full bg-black text-white py-3 rounded-xl font-bold shadow-lg active:scale-95 transition-transform">
                      סגור
                  </button>
              </div>
          </div>
      )}

    </div>
  );
}